import React, { useState, useEffect, useRef } from 'react';
import { Layers, Loader2, Printer, FileText } from 'lucide-react';
import { ToolLayout } from '../../components/ToolLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import { OnePagerPreview } from '../../components/OnePagerPreview';
import { printPacket } from '../../lib/print';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';

export default function BulkPrint() {
  const { toast } = useToast();
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const packetRef = useRef(null);

  useEffect(() => { api.get('/onepagers').then((r) => setDocs(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  const filtered = docs.filter((d) => (d.title || '').toLowerCase().includes(search.toLowerCase()));
  const selectedDocs = docs.filter((d) => selected[d.id]);
  const count = selectedDocs.length;

  const toggleAll = () => {
    if (count === filtered.length) setSelected({});
    else setSelected(Object.fromEntries(filtered.map((d) => [d.id, true])));
  };

  const doPrint = () => {
    if (!count) { toast({ title: 'Select documents first' }); return; }
    // allow the hidden packet to render, then print
    setTimeout(() => printPacket(packetRef.current), 50);
  };

  return (
    <ToolLayout title="Bulk Print Center" subtitle="Merge documents into one print-ready packet" icon={Layers}
      rightExtra={<Button size="sm" onClick={doPrint} className="bg-teal-600 hover:bg-teal-700"><Printer className="w-4 h-4 mr-1" /> Print Packet {count > 0 ? `(${count})` : ''}</Button>}>
      <Card><CardContent className="p-4">
        <div className="flex gap-3 mb-4">
          <Input placeholder="Search saved documents..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" />
          {filtered.length > 0 && <Button variant="outline" size="sm" onClick={toggleAll}>{count === filtered.length ? 'Clear all' : 'Select all'}</Button>}
        </div>
        {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>
          : filtered.length === 0 ? <p className="text-gray-500 text-sm py-6 text-center">No saved documents yet. Create some first.</p> : (
          <div className="space-y-2">{filtered.map((d) => (
            <label key={d.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-teal-50/40 cursor-pointer">
              <Checkbox checked={!!selected[d.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [d.id]: !!v }))} />
              <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: d.colorScheme?.background || '#F0FDFA' }}><FileText className="w-4 h-4" style={{ color: d.colorScheme?.primary || '#0D9488' }} /></div>
              <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">{d.title}</div><div className="text-xs text-gray-400">{d.docType}</div></div>
            </label>
          ))}</div>
        )}
        {count > 0 && <p className="text-xs text-gray-400 mt-4">{count} document{count !== 1 ? 's' : ''} selected. Click "Print Packet" to print or save them all as one PDF (one document per page).</p>}
      </CardContent></Card>

      {/* Hidden full-size packet used only for printing */}
      <div style={{ position: 'fixed', left: -99999, top: 0, width: 816 }} aria-hidden>
        <div ref={packetRef}>
          {selectedDocs.map((d) => (
            d.content && Object.keys(d.content).length > 0
              ? <OnePagerPreview key={d.id} onePager={d} />
              : (
                <div key={d.id} className="onepager-preview bg-white p-10" style={{ width: '8.5in', minHeight: '11in', fontFamily: 'Inter' }}>
                  <div className="border-b-2 pb-3 mb-3" style={{ borderColor: d.colorScheme?.primary || '#0D9488' }}>
                    <h1 className="text-2xl font-bold" style={{ color: d.colorScheme?.primary || '#0D9488' }}>{d.title}</h1>
                    <p className="text-xs text-gray-500">{d.docType}</p>
                  </div>
                  <p className="text-sm text-gray-500 italic">This document type uses a specialized layout. Open it individually from the Library to print its full design.</p>
                </div>
              )
          ))}
        </div>
      </div>
    </ToolLayout>
  );
}
